/**
 * CREATIVE DETONATOR - IDEAS MODULE (Unified)
 * Dashboard My Ideas page - using shared renderer and Generator feature modules
 *
 * Features:
 * - Shared card rendering with Phase 4
 * - State bridge: DB data → GeneratorState for feature module compatibility
 * - Full Art Director refinement
 * - Image generation/editing
 * - Alternative headlines navigation
 * - Copy prompt dropdown
 * - Filtering and sorting
 *
 * Architecture:
 * This module bridges the gap between Dashboard (DB-backed) and Generator (memory-backed)
 * by injecting DB data into GeneratorState, allowing all feature modules to work identically.
 */

// v75 - Added video preloading and debug logging
// v76 - Added VIDEOS filter button for video format ideas
// v77 - Updated delete button selector (moved to header)
// v78 - Added items-per-page selector
// v79 - Added deep linking support (?highlight=, ?view= URL params)
// v80 - Use centralized data-mapper for field normalization
// v81 - Use unified empty-state.js module
// v82 - Use timing.js debounce for search (FRONTEND-28)
// v83 - FRONTEND-21: XSS Prevention via escapeHtml
// v84 - Batch selection and deep delete with SelectionManager
// v85 - Format bar refactor: replaced filter buttons with format icons+counts, status dropdown
import { GeneratorState, loadImageConfig } from '../../generator/core/state.js';
import { escapeHtml, escapeAttr } from '../../../js/core/sanitize.js';
import { debounce } from '../../../js/core/timing.js';
import { ANIMATION, TIMEOUTS, PAGINATION } from '../../../js/core/constants.js';
import { renderPresetEmptyState } from '../../../js/shared/empty-state.js';
import { renderIdeaCard } from '../../generator/ui/idea-card.js';
import { ideasSelection } from '../../../js/core/selection-manager.js';
import { initActionBar, showConfirmModal, showToast } from '../../../js/shared/action-bar.js';
import {
  normalizeIdeaForGenerator,
  getImageUrl,
  getVideoUrl,
  isSaved,
  getTriggerCode,
  getUpdatedAt
} from '../../../js/core/data-mapper.js';
import { initImageGeneration, initImagePreviewModal } from '../../generator/features/image-generation.js';
import { initImageEdit } from '../../generator/features/image-edit.js';
import { initVideoGeneration, restoreActiveVideoPlaceholders } from '../../generator/features/video-generation.js';
import { initVersionHistory } from '../../generator/features/version-history.js';
import { initCopyPromptDropdown, openCopyDropdown } from '../../generator/ui/copy-dropdown.js';
import { api } from '../../../js/api/client.js';
// Import canvas module (self-registers to window.IdeaCanvas)
import '../../../js/shared/idea-canvas.js';
// Import manual idea module for add-manual action
import { ManualIdea } from './manual-idea.js';

const IdeasModule = (function() {
  'use strict';

  // DashboardCore refs - populated after core is ready
  let Toast, Modal, Sounds, Ideas, Brands, Sessions, Utils, Storage;

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------
  const CONFIG = {
    viewMode: 'grid',
    itemsPerPage: PAGINATION.defaultItemsPerPage,
    currentPage: 1,
    filters: {
      brand: '',
      format: '',  // Filter by format (print, video, social, etc.)
      trigger: '',
      status: '',  // Status filter: 'saved', 'has-image', 'has-video', or '' for all
      search: ''
    },
    sortBy: 'newest'
  };

  // Format definitions for filter bar
  const FORMAT_DEFINITIONS = [
    { key: 'print', label: 'PRINT', icon: 'format-icon-mini_0002_print-ooh.png' },
    { key: 'video', label: 'VIDEO', icon: 'format-icon-mini_0001_video.png' },
    { key: 'social', label: 'SOCIAL', icon: 'format-icon-mini_0000_social-media.png' },
    { key: 'digital', label: 'DIGITAL', icon: 'format-icon-mini_0003_digital.png' },
    { key: 'experiential', label: 'EXP', icon: 'format-icon-mini_0005_experimental.png' },
    { key: 'campaign', label: 'CAMPAIGN', icon: 'format-icon-mini_0004_full-campaign.png' }
  ];

  // ---------------------------------------------------------------------------
  // DOM Elements
  // ---------------------------------------------------------------------------
  let elements = {};

  // Track last bridged dataset to avoid redundant preloading
  let lastBridgedCount = null;

  // ---------------------------------------------------------------------------
  // RACE CONDITION PREVENTION - Last-call-wins for setContextForIdea
  // If user clicks rapidly on different ideas, only the most recent call wins
  // ---------------------------------------------------------------------------
  let contextOperationId = 0;

  /**
   * Wraps setContextForIdea to ensure only the most recent call completes
   * Prevents race conditions when user clicks rapidly on different ideas
   */
  function createLastCallWinsWrapper(fn) {
    return async function(...args) {
      const myOperationId = ++contextOperationId;
      try {
        await fn.apply(this, args);
        // Check if this operation is still the most recent
        if (myOperationId !== contextOperationId) {
          console.log('[Ideas] Context operation superseded, ignoring result');
          throw new Error('Operation superseded');
        }
      } catch (err) {
        if (err.message === 'Operation superseded') {
          throw err; // Re-throw to prevent .then() from executing
        }
        throw err;
      }
    };
  }

  // ---------------------------------------------------------------------------
  // STATE BRIDGE - Inject DB ideas into GeneratorState
  // ---------------------------------------------------------------------------

  /**
   * Bridge DB ideas into GeneratorState structure
   * This allows all feature modules (art-director, image-gen, etc.) to work
   * because they look at GeneratorState.generatedIdeas
   *
   * @param {Array} dbIdeas - Ideas loaded from database
   */
  function bridgeStateFromDB(dbIdeas) {
    // Skip if already bridged for this exact dataset (prevents double preloading logs)
    if (lastBridgedCount === dbIdeas.length && GeneratorState.generatedIdeas?.length > 0) {
      return;
    }
    lastBridgedCount = dbIdeas.length;

    // Create a "virtual" trigger group structure that GeneratorState expects
    // Use centralized normalizeIdeaForGenerator from data-mapper
    GeneratorState.generatedIdeas = [{
      trigger: {
        code: 'DASH',
        name: 'Dashboard Library',
        category: 'A',
        question: 'Ideas from your library'
      },
      ideas: dbIdeas.map(idea => normalizeIdeaForGenerator(idea))
    }];

    // PERFORMANCE: Single-pass initialization of all state caches
    // Previously 4 separate forEach loops + 500+ console.logs caused 3+ second delay
    if (!GeneratorState.preloadedImages) GeneratorState.preloadedImages = {};
    if (!GeneratorState.preloadedVideos) GeneratorState.preloadedVideos = {};
    if (!GeneratorState.refinedIdeas) GeneratorState.refinedIdeas = {};
    if (!GeneratorState.idMapping) GeneratorState.idMapping = {};

    let imageCount = 0;
    let videoCount = 0;
    let refinedCount = 0;

    for (const idea of dbIdeas) {
      const ideaId = String(idea.id);

      // Preload image URL
      const imageUrl = getImageUrl(idea);
      if (imageUrl) {
        GeneratorState.preloadedImages[ideaId] = imageUrl;
        imageCount++;
      }

      // Preload video URL
      const videoUrl = getVideoUrl(idea);
      if (videoUrl) {
        GeneratorState.preloadedVideos[ideaId] = videoUrl;
        videoCount++;
      }

      // Track refined ideas
      if (idea.isRefined || idea.is_refined) {
        GeneratorState.refinedIdeas[ideaId] = normalizeIdeaForGenerator(idea);
        refinedCount++;
      }

      // ID mapping (DB UUID to itself, plus frontend_id if present)
      GeneratorState.idMapping[ideaId] = ideaId;
      if (idea.frontend_id) {
        GeneratorState.idMapping[idea.frontend_id] = ideaId;
      }
    }

    console.log(`[Ideas] State populated: ${imageCount} images, ${videoCount} videos, ${refinedCount} refined`);

    // Also persist to sessionStorage for cross-module access
    try {
      const existingMapping = JSON.parse(sessionStorage.getItem('cd_id_mapping') || '{}');
      const mergedMapping = { ...existingMapping, ...GeneratorState.idMapping };
      sessionStorage.setItem('cd_id_mapping', JSON.stringify(mergedMapping));
      console.log(`[Ideas] idMapping populated for ${dbIdeas.length} ideas`);
    } catch (e) {
      console.warn('[Ideas] Could not persist idMapping to sessionStorage:', e);
    }

    // Safe access - generatedIdeas may be empty if Store wasn't ready during set
    const bridgedIdeas = GeneratorState.generatedIdeas?.[0]?.ideas;
    console.log('[Ideas] State bridged:', bridgedIdeas?.length ?? 0, 'ideas');
  }

  // NOTE: normalizeIdeaForGenerator is now imported from ../core/data-mapper.js
  // This provides centralized field normalization across all modules

  // ---------------------------------------------------------------------------
  // CONTEXT BRIDGE - Fix for "Product: Product" bug + Brand Rehydration
  // ---------------------------------------------------------------------------

  /**
   * Set GeneratorState.brief from Session data based on idea ID
   * CRITICAL: Ensures prompt builders have correct Product/Benefit/Brand context
   *
   * Without this, Dashboard prompts show "Product: Product" instead of actual product name
   * because GeneratorState.brief is not populated from the DB session data.
   *
   * V5 ENHANCEMENT: Also rehydrates brand context so refinement, video generation,
   * and other features use the correct brand DNA even for old ideas.
   *
   * @param {string} ideaId - The idea ID to get context for
   */
  async function setContextForIdea(ideaId) {
    // Find the idea
    const idea = Ideas.items.find(i => String(i.id) === String(ideaId));
    if (!idea) {
      console.warn('[Ideas] Context bridge: idea not found', ideaId);
      return;
    }

    // Find the associated session (may be null for manual ideas)
    const sessionId = idea.sessionId || idea.session_id;
    let session = null;

    if (sessionId) {
      session = Sessions.items.find(s => s.id === sessionId);
      if (!session) {
        console.warn('[Ideas] Context bridge: session not found', sessionId);
      }
    }

    // Extract brief from session or idea meta (for manual ideas)
    // Session stores brief as object or sometimes just as string (legacy)
    let product = '', benefit = '', formats = [], productId = null, productData = null;

    if (session?.brief) {
      if (typeof session.brief === 'string') {
        // Legacy: brief was stored as just product name
        product = session.brief;
      } else {
        product = session.brief.product || '';
        benefit = session.brief.benefit || '';
        formats = session.brief.formats || [];
        productId = session.brief.productId || null;
        productData = session.brief.productData || null;
      }
    }

    // For manual ideas without session, try to get data from idea meta
    if (!session && idea.meta) {
      product = idea.meta.product || idea.headline || '';
      benefit = idea.meta.benefit || '';
      // Check both idea.meta.brief.productId (generated ideas) and idea.meta.productId (manual ideas)
      productId = idea.meta.brief?.productId || idea.meta.productId || idea.product_id || null;
      productData = idea.meta.productData || null;
      console.log('[Ideas] Context bridge: using idea meta for manual idea', ideaId);
    }

    // Also check idea.meta.brief for productId and productData (stored during generation)
    // This handles cases where session is found but productId wasn't in session.brief
    if (!productId && idea.meta?.brief?.productId) {
      productId = idea.meta.brief.productId;
      console.log(`[Ideas] Found productId in idea.meta.brief:`, productId);
    }
    if (!productData && idea.meta?.brief?.productData) {
      productData = idea.meta.brief.productData;
      console.log(`[Ideas] Found productData in idea.meta.brief:`, productData?.description?.substring(0, 50) || 'null');
    }

    // Fallbacks for alternative schema patterns
    if (!product && session?.product) product = session.product;
    if (!benefit && session?.benefit) benefit = session.benefit;
    if (!formats.length && session?.formats) formats = session.formats;
    if (!formats.length && idea.format) formats = [idea.format];

    // ---------------------------------------------------------------------------
    // V5 BRAND CONTEXT REHYDRATION
    // CRITICAL: When clicking ideas from Dashboard to Refine or Make Video,
    // the brand DNA must be injected so prompts use brand voice/visual style.
    // Works for both session-based and manual ideas.
    // ---------------------------------------------------------------------------
    let brand = null;
    const brandId = idea.brandId || idea.brand_id || session?.brand_id || session?.brandId;

    if (brandId && Brands) {
      // Try to get brand from Dashboard's loaded brands
      brand = Brands.getById(brandId);

      if (brand) {
        // Build brand context for prompts AND modal display
        // Includes shell_color and colors for UI cartridge rendering
        brand = {
          id: brand.id,
          code: brand.code,
          name: brand.name,
          industry: brand.industry,
          description: brand.description,
          archetype: brand.archetype,
          tone: brand.tone,
          voice_prompt: brand.voice_prompt || brand.voicePrompt,
          visual_prompt: brand.visual_prompt || brand.visualPrompt,
          colors: brand.colors || [],
          shell_color: brand.shell_color || brand.shellColor || 'slate'
        };
        console.log(`[Ideas] Brand context rehydrated: ${brand.name} (${brand.code}) shell=${brand.shell_color}`);

        // ---------------------------------------------------------------------------
        // FETCH PRODUCT DATA IF MISSING (for old ideas without productData)
        // ---------------------------------------------------------------------------
        if (!productData && productId) {
          console.log(`[Ideas] Fetching product for productId: ${productId}`);
          try {
            const response = await api.getProducts(brandId);
            if (response.success && response.products) {
              const foundProduct = response.products.find(p => p.id === productId);
              if (foundProduct) {
                productData = foundProduct;
                console.log(`[Ideas] Fetched productData:`, foundProduct.name, `- ${foundProduct.description?.substring(0, 50) || 'no desc'}`);
              }
            }
          } catch (err) {
            console.warn(`[Ideas] Failed to fetch product:`, err.message);
          }
        }
      } else {
        console.warn(`[Ideas] Brand ${brandId} not found in cache`);
      }
    }

    // Inject into GeneratorState
    GeneratorState.brief = { product, benefit, formats, brand, productId, productData };

    // Also set currentBrand for modules that check it directly
    GeneratorState.currentBrand = brand;
    GeneratorState.sessionBrand = brand;

    // Set sessionProduct for context bar and prompts
    // FIXED: Create minimal product object when we only have product name string
    // This ensures the context bar can display the product name even without full productData
    if (productData) {
      // Full product data from DB
      GeneratorState.sessionProduct = productData;
      GeneratorState.selectedProduct = productData;
      console.log(`[Ideas] Set sessionProduct (full):`, productData.name, `- ${productData.description?.substring(0, 50) || 'no desc'}`);
    } else if (product) {
      // Create minimal product object from product name string
      // This allows the context bar to show "Product: KURT SUPER FOOD" even without full DB product record
      const minimalProduct = {
        name: product,
        description: benefit || null  // Use benefit as fallback description
      };
      GeneratorState.sessionProduct = minimalProduct;
      GeneratorState.selectedProduct = minimalProduct;
      console.log(`[Ideas] Set sessionProduct (minimal):`, product);
    } else {
      GeneratorState.sessionProduct = null;
      GeneratorState.selectedProduct = null;
    }

    // DEBUG: Log full brief info
    console.log(`[Ideas] Context set for ${ideaId}:`, {
      product: product || '(empty)',
      benefit: benefit || '(empty)',
      formats,
      brand: brand ? `${brand.name} (${brand.code})` : '(none)',
      productData: productData ? `${productData.name} - ${productData.description?.substring(0, 30)}...` : '(none)',
      sessionBrief: session?.brief || '(manual idea - no session)',
      isManual: idea.meta?.source === 'manual'
    });
  }

  // Wrapped version with race condition prevention
  // Only the most recent call's result is used, preventing state corruption from rapid clicks
  const safeSetContextForIdea = createLastCallWinsWrapper(setContextForIdea);

  // ---------------------------------------------------------------------------
  // Rendering - Uses Shared Renderer
  // ---------------------------------------------------------------------------

  /**
   * Render the ideas grid using the shared renderer
   * @param {Array} ideas - Ideas to render
   */
  function renderIdeasGrid(ideas) {
    if (!elements.grid) return;

    if (ideas.length === 0) {
      elements.grid.innerHTML = renderPresetEmptyState('ideas');
      return;
    }

    elements.grid.className = 'ideas-grid';

    // Use shared renderer with isDashboard flag
    elements.grid.innerHTML = ideas.map((idea, index) =>
      renderIdeaCard(idea, index + 1, { isDashboard: true })
    ).join('');

    // Bind dashboard-specific events (save, delete)
    // Feature module events (refine, generate, edit) are handled by their own delegation
    bindCardEvents();

    // Restore video placeholders for any active generations
    // (placeholders get lost when grid re-renders, e.g., after filtering)
    restoreActiveVideoPlaceholders();
  }

  // ---------------------------------------------------------------------------
  // Event Binding
  // ---------------------------------------------------------------------------

  /**
   * Bind dashboard-specific card events
   * Feature modules handle their own events via delegation
   */
  function bindCardEvents() {
    if (!elements.grid) return;

    // Save/unsave buttons
    elements.grid.querySelectorAll('.idea-save-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleSaveIdea(btn.dataset.id, btn);
      });
    });

    // Delete buttons (Dashboard only - now in header)
    elements.grid.querySelectorAll('.idea-delete-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteIdea(btn.dataset.id);
      });
    });

    // Card click for selection (optional - for future bulk operations)
    elements.grid.querySelectorAll('.idea-card').forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't trigger on interactive elements
        if (e.target.closest('.btn-canvas-view') ||
            e.target.closest('.btn-view-idea-large') ||
            e.target.closest('.idea-copy-btn') ||
            e.target.closest('.idea-save-btn') ||
            e.target.closest('.headline-nav-btn') ||
            e.target.closest('.btn-generate-img') ||
            e.target.closest('.btn-img-action') ||
            e.target.closest('.idea-delete-btn')) {
          return;
        }
        // Future: toggle selection for bulk operations
        // card.classList.toggle('selected');
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Card Actions
  // ---------------------------------------------------------------------------

  /**
   * Toggle save/unsave an idea
   * @param {string} ideaId - Idea ID
   * @param {HTMLElement} button - The save button element
   */
  async function toggleSaveIdea(ideaId, button) {
    const idea = Ideas.getById(ideaId);
    if (!idea) return;

    const wasSaved = isSaved(idea);
    const newSavedState = !wasSaved;

    try {
      if (wasSaved) {
        await api.unsaveIdea(ideaId);
      } else {
        await api.saveIdea(ideaId);
      }

      // Update local state (both field names for compatibility)
      idea.saved = newSavedState;
      idea.is_saved = newSavedState;

      // Update timestamp for sorting
      idea.updatedAt = new Date().toISOString();

      // Update button appearance
      if (button) {
        button.classList.toggle('saved', newSavedState);
        button.title = newSavedState ? 'Unsave' : 'Save';
      }

      Toast.success(wasSaved ? 'Idea unsaved' : 'Idea saved');
      Sounds?.play('success');

      // If viewing saved filter and item was unsaved, re-apply filters to remove from view
      if (CONFIG.filters.status === 'saved' && !newSavedState) {
        applyFilters();
      } else {
        updateFilterCounts(Ideas.items.length);
      }
    } catch (error) {
      console.error('[Ideas] Save toggle failed:', error);
      Toast.error('Failed to update idea');
      Sounds?.play('error');
    }
  }

  /**
   * Delete an idea with confirmation
   * @param {string} ideaId - Idea ID
   */
  function deleteIdea(ideaId) {
    Modal.confirm({
      title: 'Delete Idea',
      message: 'Are you sure you want to delete this idea? This cannot be undone.',
      confirmText: 'Delete',
      confirmClass: 'btn-danger',
      onConfirm: async () => {
        try {
          await Ideas.delete(ideaId);
          Toast.success('Idea deleted');
          Sounds?.play('success');

          // Reload and re-bridge state
          await loadAndRender();
        } catch (error) {
          console.error('[Ideas] Delete failed:', error);
          Toast.error('Failed to delete idea');
          Sounds?.play('error');
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Filtering & Sorting
  // ---------------------------------------------------------------------------

  /**
   * Core filtering logic - shared between applyFilters and getFilteredIdeasForDeepLink
   * Extracts common filter patterns to eliminate code duplication
   *
   * @param {Array} ideas - Ideas to filter
   * @param {Object} filters - Filter options { brand, format, status }
   * @returns {Array} Filtered ideas
   */
  function filterIdeasByBasicCriteria(ideas, filters = {}) {
    let filtered = [...ideas];

    // Brand filter
    if (filters.brand) {
      filtered = filtered.filter(idea => idea.brandId === filters.brand);
    }

    // Format filter
    if (filters.format) {
      filtered = filtered.filter(idea =>
        idea.format?.toLowerCase() === filters.format.toLowerCase()
      );
    }

    // Status filter (saved/has-image/has-video)
    if (filters.status === 'saved') {
      filtered = filtered.filter(idea => isSaved(idea));
    } else if (filters.status === 'has-image') {
      filtered = filtered.filter(idea => getImageUrl(idea));
    } else if (filters.status === 'has-video') {
      filtered = filtered.filter(idea =>
        idea.format?.toLowerCase() === 'video' || getVideoUrl(idea)
      );
    }

    return filtered;
  }

  /**
   * Sort ideas by the specified sort order
   * @param {Array} ideas - Ideas to sort (mutates in place)
   * @param {string} sortBy - Sort order: 'newest', 'oldest', 'headline'
   */
  function sortIdeas(ideas, sortBy) {
    ideas.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          const aOldest = getUpdatedAt(a) || a.createdAt || 0;
          const bOldest = getUpdatedAt(b) || b.createdAt || 0;
          return new Date(aOldest) - new Date(bOldest);
        case 'headline':
          return (a.headline || '').localeCompare(b.headline || '');
        default: // newest
          const aNewest = getUpdatedAt(a) || a.createdAt || 0;
          const bNewest = getUpdatedAt(b) || b.createdAt || 0;
          return new Date(bNewest) - new Date(aNewest);
      }
    });
  }

  /**
   * Apply current filters and re-render
   */
  function applyFilters() {
    // Apply basic filters (brand, format, status)
    let filtered = filterIdeasByBasicCriteria(Ideas.items, {
      brand: CONFIG.filters.brand,
      format: CONFIG.filters.format,
      status: CONFIG.filters.status
    });

    // Search filter - searches across all text fields including session data
    if (CONFIG.filters.search) {
      const searchLower = CONFIG.filters.search.toLowerCase();
      filtered = filtered.filter(idea => {
        // Get brand name if idea has brandId
        const brand = idea.brandId ? Brands.getById(idea.brandId) : null;
        const brandName = brand?.name?.toLowerCase() || '';

        // Get session data (product, benefit) via sessionId
        const session = idea.sessionId ? Sessions.items.find(s => s.id === idea.sessionId) : null;
        const productName = session?.brief?.product?.toLowerCase() || '';
        const benefitText = session?.brief?.benefit?.toLowerCase() || '';

        // Search across all relevant fields (use normalized field names)
        return (
          // Idea fields
          idea.headline?.toLowerCase().includes(searchLower) ||
          idea.visual?.toLowerCase().includes(searchLower) ||
          idea.title?.toLowerCase().includes(searchLower) ||
          idea.whyItWorks?.toLowerCase().includes(searchLower) ||
          idea.format?.toLowerCase().includes(searchLower) ||
          getTriggerCode(idea).toLowerCase().includes(searchLower) ||
          idea.sourceFragment?.toLowerCase().includes(searchLower) ||
          idea.sourceTruth?.toLowerCase().includes(searchLower) ||
          // Brand name
          brandName.includes(searchLower) ||
          // Session data (product & benefit)
          productName.includes(searchLower) ||
          benefitText.includes(searchLower)
        );
      });
    }

    // Sort filtered results
    sortIdeas(filtered, CONFIG.sortBy);

    // CRITICAL: Always bridge ALL ideas for feature modules (copy, refine, image gen, prompts)
    // This ensures any idea can be found regardless of current filter state
    bridgeStateFromDB(Ideas.items);

    // Get all normalized ideas from state
    const allNormalized = GeneratorState.generatedIdeas[0]?.ideas || [];

    // Create display subset by matching filtered IDs (preserves sort order from filtered array)
    const displayIdeas = filtered.map(filteredIdea => {
      return allNormalized.find(n => String(n.id) === String(filteredIdea.id));
    }).filter(Boolean);

    // Pagination on the filtered/sorted display set
    const startIndex = (CONFIG.currentPage - 1) * CONFIG.itemsPerPage;
    const paginatedIdeas = displayIdeas.slice(startIndex, startIndex + CONFIG.itemsPerPage);

    renderIdeasGrid(paginatedIdeas);
    renderPagination(filtered.length);
    updateFilterCounts(filtered.length);
  }

  /**
   * Render pagination controls
   * @param {number} totalItems - Total filtered items count
   */
  function renderPagination(totalItems) {
    const paginationEl = document.querySelector('.pagination');
    if (!paginationEl) return;

    const totalPages = Math.ceil(totalItems / CONFIG.itemsPerPage);
    if (totalPages <= 1) {
      paginationEl.innerHTML = '';
      return;
    }

    let html = `<button class="pagination-btn" ${CONFIG.currentPage === 1 ? 'disabled' : ''} data-page="prev">← Prev</button>`;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - CONFIG.currentPage) <= 1) {
        html += `<button class="pagination-btn ${i === CONFIG.currentPage ? 'active' : ''}" data-page="${i}">${i}</button>`;
      } else if (i === 2 || i === totalPages - 1) {
        html += '<span class="pagination-ellipsis">...</span>';
      }
    }

    html += `<button class="pagination-btn" ${CONFIG.currentPage === totalPages ? 'disabled' : ''} data-page="next">Next →</button>`;
    paginationEl.innerHTML = html;

    // Store totalPages for the delegated handler
    paginationEl.dataset.totalPages = totalPages;
  }

  /**
   * Initialize pagination event delegation (called once on init)
   * Uses event delegation to prevent memory leaks from recreating listeners on each render
   */
  function initPaginationDelegation() {
    const paginationEl = document.querySelector('.pagination');
    if (!paginationEl || paginationEl._delegated) return;

    paginationEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.pagination-btn');
      if (!btn || btn.disabled) return;

      const page = btn.dataset.page;
      const totalPages = parseInt(paginationEl.dataset.totalPages) || 1;

      if (page === 'prev' && CONFIG.currentPage > 1) {
        CONFIG.currentPage--;
      } else if (page === 'next' && CONFIG.currentPage < totalPages) {
        CONFIG.currentPage++;
      } else if (!isNaN(page)) {
        CONFIG.currentPage = parseInt(page);
      }
      applyFilters();
      Sounds?.play('click');
    });

    // Mark as delegated to prevent duplicate listeners
    paginationEl._delegated = true;
  }

  /**
   * Calculate counts for each format
   * @returns {Object} Format counts { all: n, print: n, video: n, ... }
   */
  function calculateFormatCounts() {
    const allIdeas = Ideas.items;
    const counts = { all: allIdeas.length };

    // Count each format
    FORMAT_DEFINITIONS.forEach(fmt => {
      counts[fmt.key] = allIdeas.filter(idea =>
        idea.format?.toLowerCase() === fmt.key
      ).length;
    });

    return counts;
  }

  /**
   * Render format filter buttons dynamically
   * Called on init and when ideas change
   */
  function renderFormatFilterButtons() {
    const container = document.getElementById('format-filter-group');
    if (!container) return;

    const counts = calculateFormatCounts();
    const activeFormat = CONFIG.filters.format;
    const activeStatus = CONFIG.filters.status;

    // Count saved ideas
    const savedCount = Ideas.items.filter(i => isSaved(i)).length;

    // Build HTML for ALL button + SAVED button + format buttons
    // ALL button (no format, no status)
    const isAllActive = !activeFormat && !activeStatus;
    let html = `
      <button class="ideas-filter-btn ${isAllActive ? 'active' : ''}" data-format="" data-status="" title="All Ideas">
        <span class="filter-label">ALL</span>
        <span class="filter-count">${counts.all}</span>
      </button>
    `;

    // SAVED button (special status filter)
    const isSavedActive = activeStatus === 'saved';
    html += `
      <button class="ideas-filter-btn ${isSavedActive ? 'active' : ''}" data-format="" data-status="saved" title="Saved Ideas">
        <svg class="filter-icon" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1l2 5h5l-4 3 2 5-5-3-5 3 2-5-4-3h5z"/></svg>
        <span class="filter-count">${savedCount}</span>
      </button>
    `;

    // Add a button for each format (icon + count only, no text label)
    FORMAT_DEFINITIONS.forEach(fmt => {
      const isActive = activeFormat === fmt.key && !activeStatus;
      const count = counts[fmt.key] || 0;
      html += `
        <button class="ideas-filter-btn ${isActive ? 'active' : ''}" data-format="${fmt.key}" data-status="" title="${fmt.label}">
          <img src="/assets/icons/${fmt.icon}" alt="" class="filter-icon-img">
          <span class="filter-count">${count}</span>
        </button>
      `;
    });

    container.innerHTML = html;

    // Bind click events to format buttons
    container.querySelectorAll('.ideas-filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        // Update active state
        container.querySelectorAll('.ideas-filter-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Update filters
        CONFIG.filters.format = btn.dataset.format || '';
        CONFIG.filters.status = btn.dataset.status || '';
        CONFIG.currentPage = 1;
        applyFilters();
        Sounds?.play('click');
      });
    });
  }

  /**
   * Update filter count displays
   * @param {number} filteredCount - Current filtered count
   */
  function updateFilterCounts(filteredCount) {
    // Re-render format buttons with updated counts
    renderFormatFilterButtons();
  }

  /**
   * Reset format filter to "ALL"
   * Used by deep link handler when target is hidden by filters
   */
  function resetFormatFilter() {
    CONFIG.filters.format = '';
    CONFIG.filters.status = '';
    // Re-render format buttons will be done by applyFilters -> updateFilterCounts
  }

  /**
   * Update sort button active states
   * @param {string} active - Active sort: 'newest' or 'oldest'
   */
  function updateSortButtons(active) {
    if (elements.sortNewest) {
      elements.sortNewest.classList.toggle('active', active === 'newest');
    }
    if (elements.sortOldest) {
      elements.sortOldest.classList.toggle('active', active === 'oldest');
    }
  }

  // ---------------------------------------------------------------------------
  // Load and Render
  // ---------------------------------------------------------------------------

  /**
   * Load data from DB and render
   */
  async function loadAndRender() {
    await Ideas.load();
    await Brands.load();
    await Sessions.load();

    // CRITICAL: Bridge DB data to GeneratorState
    bridgeStateFromDB(Ideas.items);

    // Use accessor functions for consistent field access
    const withImages = Ideas.items.filter(i => getImageUrl(i));
    const withVideos = Ideas.items.filter(i => getVideoUrl(i));
    console.log('[Ideas] Loaded', Ideas.items.length, 'ideas,', withImages.length, 'with images,', withVideos.length, 'with videos');

    // Debug: Log first idea with video to verify DB response
    const firstWithVideo = Ideas.items.find(i => getVideoUrl(i));
    if (firstWithVideo) {
      console.log('[Ideas] Sample idea with video:', firstWithVideo.id, 'videoUrl:', getVideoUrl(firstWithVideo));
    }

    applyFilters();

    // Handle deep linking after initial render
    handleDeepLink();
  }

  // ---------------------------------------------------------------------------
  // DEEP LINKING - Handle URL parameters for navigation
  // ---------------------------------------------------------------------------

  /**
   * Check URL for deep link parameters and navigate to specific idea
   * Supports: ?highlight=<ideaId> - scroll to and highlight idea card
   *           ?view=<ideaId> - scroll to, highlight, and open canvas
   *           ?action=add-manual - open manual idea modal
   */
  function handleDeepLink() {
    const urlParams = new URLSearchParams(window.location.search);
    const highlightId = urlParams.get('highlight');
    const viewId = urlParams.get('view');
    const action = urlParams.get('action');

    // Handle action=add-manual: open manual idea modal
    if (action === 'add-manual') {
      console.log('[Ideas] Deep link action: add-manual - opening modal');
      // Initialize and open manual idea modal
      ManualIdea.init().then(() => {
        ManualIdea.open();
        // Clean URL after opening modal
        cleanDeepLinkUrl();
      }).catch(err => {
        console.error('[Ideas] Failed to initialize ManualIdea:', err);
      });
      return;
    }

    const targetId = viewId || highlightId;
    if (!targetId) return;

    console.log('[Ideas] Deep link detected:', { highlightId, viewId });

    // Wait for DOM to settle after render
    requestAnimationFrame(() => {
      setTimeout(() => {
        // 1. Find the idea in our data
        const idea = Ideas.items.find(i => String(i.id) === String(targetId));
        if (!idea) {
          console.warn('[Ideas] Deep link target not found:', targetId);
          Toast?.warning('Idea not found in current view');
          cleanDeepLinkUrl();
          return;
        }

        // 2. Ensure filters don't hide the target (clear filters if needed)
        if (!isIdeaVisible(targetId)) {
          console.log('[Ideas] Target hidden by filters, clearing filters');
          CONFIG.filters.status = '';
          CONFIG.filters.brand = '';
          CONFIG.filters.format = '';
          CONFIG.filters.search = '';
          CONFIG.currentPage = 1;
          resetFormatFilter();
          applyFilters();
        }

        // 3. Calculate which page the idea is on
        const allFiltered = getFilteredIdeasForDeepLink();
        const ideaIndex = allFiltered.findIndex(i => String(i.id) === String(targetId));
        if (ideaIndex >= 0) {
          const targetPage = Math.floor(ideaIndex / CONFIG.itemsPerPage) + 1;
          if (targetPage !== CONFIG.currentPage) {
            CONFIG.currentPage = targetPage;
            applyFilters();
          }
        }

        // 4. Wait for render, then find card in DOM
        requestAnimationFrame(() => {
          const card = document.querySelector(`.idea-card[data-id="${targetId}"]`);

          if (card) {
            // Scroll to card
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Add highlight
            card.classList.add('deep-link-highlight');

            // Remove highlight after animation
            setTimeout(() => {
              card.classList.remove('deep-link-highlight');
            }, ANIMATION.highlightDuration);

            // If ?view= param, also open canvas after highlight is visible
            if (viewId && window.IdeaCanvas) {
              setTimeout(async () => {
                try {
                  const normalized = normalizeIdeaForGenerator(idea);
                  await safeSetContextForIdea(targetId);
                  window.IdeaCanvas.open(normalized);
                } catch (err) {
                  if (err.message === 'Operation superseded') return; // Ignore - newer operation took over
                  console.error('[Ideas] Failed to open canvas from deep link:', err);
                  Toast?.error('Failed to load idea');
                }
              }, ANIMATION.openAfterHighlight);
            }

            Sounds?.play('success');
          } else {
            console.warn('[Ideas] Card not found in DOM after render:', targetId);
          }

          // Clean URL without reload
          cleanDeepLinkUrl();
        });
      }, 100);
    });
  }

  /**
   * Check if an idea is currently visible (not hidden by filters)
   */
  function isIdeaVisible(ideaId) {
    const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
    return !!card;
  }

  /**
   * Get filtered ideas without re-rendering (for deep link pagination calculation)
   * Uses shared filterIdeasByBasicCriteria helper to eliminate duplicate code
   */
  function getFilteredIdeasForDeepLink() {
    // Apply basic filters (reuses shared helper)
    let filtered = filterIdeasByBasicCriteria(Ideas.items, {
      brand: CONFIG.filters.brand,
      format: CONFIG.filters.format,
      status: CONFIG.filters.status
    });

    // Sort same as applyFilters
    sortIdeas(filtered, CONFIG.sortBy);

    return filtered;
  }

  /**
   * Remove URL parameters without page reload
   */
  function cleanDeepLinkUrl() {
    const cleanPath = window.location.pathname;
    window.history.replaceState({}, document.title, cleanPath);
  }

  // ---------------------------------------------------------------------------
  // Initialize
  // ---------------------------------------------------------------------------

  /**
   * Initialize the Ideas module
   */
  async function init() {
    // Wait for DashboardCore to be ready
    if (!window.DashboardCore || !window.DashboardCore.isDashboardReady()) {
      console.log('[Ideas] Waiting for dashboard to be ready...');
      if (window.DashboardCore?.waitForDashboard) {
        const ready = await window.DashboardCore.waitForDashboard(TIMEOUTS.dashboardReady);
        if (!ready) console.error('[Ideas] Dashboard timeout - proceeding anyway');
      } else {
        await new Promise(resolve => {
          const timeout = setTimeout(() => {
            console.warn('[Ideas] Dashboard wait timeout');
            resolve();
          }, TIMEOUTS.dashboardReady);
          if (window.DashboardCore?.isDashboardReady()) {
            clearTimeout(timeout);
            resolve();
            return;
          }
          window.addEventListener('dashboard:ready', () => {
            clearTimeout(timeout);
            resolve();
          }, { once: true });
        });
      }
    }

    if (!window.DashboardCore) {
      console.error('[Ideas] DashboardCore not available');
      return;
    }

    // Get DashboardCore references
    ({ Toast, Modal, Sounds, Ideas, Brands, Sessions, Utils, Storage } = window.DashboardCore);

    console.log('[Ideas] Dashboard ready, loading data...');

    // Cache DOM elements
    elements = {
      grid: document.getElementById('ideas-grid'),
      filterBrand: document.getElementById('filter-brand'),
      sortNewest: document.getElementById('sort-newest'),
      sortOldest: document.getElementById('sort-oldest'),
      filterSearch: document.getElementById('filter-search'),
      searchClear: document.getElementById('search-clear'),
      searchWrapper: document.querySelector('.header-search-wrapper')
    };

    // CRITICAL: Initialize canvas BEFORE loading data
    // This fixes the race condition where handleDeepLink() tries to use IdeaCanvas
    // before it's initialized (deep linking from Gallery → Ideas)
    if (window.IdeaCanvas) {
      window.IdeaCanvas.init('ideas-grid', 'canvas-view');
    }

    // Initialize pagination event delegation (once, prevents memory leaks)
    initPaginationDelegation();

    // Load data and bridge state (calls handleDeepLink at the end)
    await loadAndRender();

    // Load image config from backend (enables Art Director regenerate checkbox)
    await loadImageConfig().catch(err => {
      console.warn('[Ideas] Could not load image config:', err.message);
    });

    // CRITICAL: Initialize feature modules (same as Phase 4!)
    // These modules use event delegation, so they work on dynamically rendered cards
    initImageGeneration();
    initImagePreviewModal();
    initImageEdit();
    initVideoGeneration();
    initVersionHistory();
    initCopyPromptDropdown();

    // NOTE: IdeaCanvas is now initialized earlier (before loadAndRender)
    // to fix deep linking race condition

    // ---------------------------------------------------------------------------
    // CONTEXT BRIDGE EVENT DELEGATION
    // Intercept clicks on feature buttons to set state BEFORE modules run
    // Uses capture phase to ensure this runs first
    // FIXED: Attach to document to catch both Grid (#ideas-grid) AND Canvas (#canvas-view) clicks
    // ---------------------------------------------------------------------------
    document.addEventListener('click', (e) => {
      // Guard: Only run if we're on the dashboard (not generator page)
      if (!document.body.classList.contains('dashboard-body')) return;

      // 0. Canvas View Button - Opens detail modal
      const canvasBtn = e.target.closest('.btn-canvas-view');
      if (canvasBtn) {
        e.stopPropagation();
        const ideaId = canvasBtn.dataset.id;
        const idea = Ideas.items.find(i => String(i.id) === String(ideaId));

        if (idea && window.IdeaCanvas) {
          const normalized = normalizeIdeaForGenerator(idea);
          // Await context (may fetch product from API) - uses safe wrapper for race prevention
          safeSetContextForIdea(ideaId)
            .then(() => {
              Utils.haptic?.(12);
              window.IdeaCanvas.open(normalized);
            })
            .catch(err => {
              if (err.message === 'Operation superseded') return; // Ignore - newer operation took over
              console.error('[Ideas] Failed to set context for canvas:', err);
              Toast?.error('Failed to load idea context');
            });
        }
        return;
      }

      // 0b. VIEW IDEA Large Button - Same as canvas view
      const viewIdeaBtn = e.target.closest('.btn-view-idea-large');
      if (viewIdeaBtn) {
        e.stopPropagation();
        const ideaId = viewIdeaBtn.dataset.id;
        const idea = Ideas.items.find(i => String(i.id) === String(ideaId));

        if (idea && window.IdeaCanvas) {
          const normalized = normalizeIdeaForGenerator(idea);
          // Await context (may fetch product from API) - uses safe wrapper for race prevention
          safeSetContextForIdea(ideaId)
            .then(() => {
              Utils.haptic?.(12);
              window.IdeaCanvas.open(normalized);
            })
            .catch(err => {
              if (err.message === 'Operation superseded') return; // Ignore - newer operation took over
              console.error('[Ideas] Failed to set context for view:', err);
              Toast?.error('Failed to load idea context');
            });
        }
        return;
      }

      // 1. Generate Image Button (AND Regenerate button AND Canvas placeholder)
      // TRAFFIC COP: Dashboard is the authoritative handler for this button
      // We use stopImmediatePropagation + preventDefault to ensure the global
      // handler in image-generation.js does NOT fire (race condition fix)
      // FIXED: Added .btn-regenerate-img selector for the "+" button in overlays
      // FIXED: Added .btn-canvas-add-image selector for big placeholder button in Canvas
      const imgBtn = e.target.closest('.btn-generate-img, .btn-regenerate-img, .btn-canvas-add-image');
      if (imgBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Try button data attributes first, then fall back to IdeaCanvas current idea
        let ideaId = imgBtn.dataset.ideaId || imgBtn.dataset.id;
        if (!ideaId && window.IdeaCanvas?.isOpen?.()) {
          const canvasIdea = window.IdeaCanvas.getCurrentIdea?.();
          ideaId = canvasIdea?.id;
        }
        console.log('[Ideas] Intercepted Generate Image click for:', ideaId);
        if (!ideaId) {
          console.warn('[Ideas] No idea ID found for generate image button');
          return;
        }
        // Await context (may fetch product from API) BEFORE opening modal - uses safe wrapper
        safeSetContextForIdea(ideaId)
          .then(() => {
            console.log('[Ideas] Context loaded, opening modal for:', ideaId);
            if (window.openImageGenModal) {
              window.openImageGenModal(ideaId);
            }
          })
          .catch(err => {
            if (err.message === 'Operation superseded') return; // Ignore - newer operation took over
            console.error('[Ideas] Failed to set context for image gen:', err);
            Toast?.error('Failed to load idea context');
          });
        return;
      }

      // 2. Make Video Button
      // TRAFFIC COP: Dashboard handles video button to load context first
      const videoBtn = e.target.closest('.btn-make-video');
      if (videoBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const ideaId = videoBtn.dataset.id;
        console.log('[Ideas] Intercepted Make Video click for:', ideaId);
        // Await context (may fetch product from API) BEFORE opening modal - uses safe wrapper
        safeSetContextForIdea(ideaId)
          .then(() => {
            console.log('[Ideas] Context loaded, opening video modal for:', ideaId);
            if (window.openVideoGenModal) {
              window.openVideoGenModal(ideaId);
            }
          })
          .catch(err => {
            if (err.message === 'Operation superseded') return; // Ignore - newer operation took over
            console.error('[Ideas] Failed to set context for video gen:', err);
            Toast?.error('Failed to load idea context');
          });
        return;
      }

      // 3. Copy Prompt Dropdown
      const copyBtn = e.target.closest('.idea-copy-btn');
      if (copyBtn) {
        e.stopPropagation();
        const ideaId = copyBtn.dataset.id;
        safeSetContextForIdea(ideaId)
          .then(() => {
            openCopyDropdown(ideaId);
          })
          .catch(err => {
            if (err.message === 'Operation superseded') return; // Ignore - newer operation took over
            console.error('[Ideas] Failed to set context for copy:', err);
            Toast?.error('Failed to load idea context');
          });
        return;
      }

    }, true); // Use capture phase to ensure this runs before module listeners

    // Brand filter dropdown
    // FRONTEND-21: Escape brand names in dropdown
    if (elements.filterBrand) {
      elements.filterBrand.innerHTML = `
        <option value="">All Brands</option>
        ${Brands.items.map(b => `<option value="${escapeAttr(b.id)}">${escapeHtml(b.name)}</option>`).join('')}
      `;
      elements.filterBrand.addEventListener('change', (e) => {
        CONFIG.filters.brand = e.target.value;
        CONFIG.currentPage = 1;
        applyFilters();
      });
    }

    // Render format filter buttons (dynamically)
    renderFormatFilterButtons();

    // Search input with debounce (using timing.js)
    if (elements.filterSearch) {
      // Debounced search handler (300ms delay)
      const debouncedSearch = debounce((value) => {
        CONFIG.filters.search = value;
        CONFIG.currentPage = 1;
        applyFilters();
      }, 300);

      elements.filterSearch.addEventListener('input', (e) => {
        const value = e.target.value.trim();

        // Toggle clear button visibility (immediate)
        if (elements.searchWrapper) {
          elements.searchWrapper.classList.toggle('has-value', value.length > 0);
        }

        // Debounced filter update
        debouncedSearch(value);
      });

      // Clear search button
      if (elements.searchClear) {
        elements.searchClear.addEventListener('click', () => {
          elements.filterSearch.value = '';
          CONFIG.filters.search = '';
          CONFIG.currentPage = 1;
          if (elements.searchWrapper) {
            elements.searchWrapper.classList.remove('has-value');
          }
          applyFilters();
          elements.filterSearch.focus();
        });
      }

      // Keyboard shortcut: Escape to clear
      elements.filterSearch.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          elements.searchClear?.click();
        }
      });
    }

    // Sort arrow buttons
    if (elements.sortNewest) {
      elements.sortNewest.addEventListener('click', () => {
        CONFIG.sortBy = 'newest';
        updateSortButtons('newest');
        applyFilters();
        Sounds?.play('click');
      });
    }

    if (elements.sortOldest) {
      elements.sortOldest.addEventListener('click', () => {
        CONFIG.sortBy = 'oldest';
        updateSortButtons('oldest');
        applyFilters();
        Sounds?.play('click');
      });
    }

    // Items per page selector
    document.getElementById('items-per-page')?.addEventListener('change', (e) => {
      CONFIG.itemsPerPage = parseInt(e.target.value);
      CONFIG.currentPage = 1;
      applyFilters();
    });

    // =========================================================================
    // BATCH SELECTION - Initialize SelectionManager and ActionBar
    // =========================================================================

    // Clear any previous selection state
    ideasSelection.clear();

    // Bind click-to-select on idea cards (uses event delegation)
    ideasSelection.bindToCards(
      '#ideas-grid',
      '.idea-card',
      'data-id',
      {
        onSelect: (id, isSelected, card) => {
          Sounds?.play('click');
        }
      }
    );

    // Initialize action bar with batch operations
    initActionBar({
      selection: ideasSelection,
      containerId: 'ideas-action-bar',
      getAllIds: () => {
        // Get all currently displayed idea IDs from the grid
        return Array.from(document.querySelectorAll('#ideas-grid .idea-card'))
          .map(card => card.dataset.id)
          .filter(Boolean);
      },
      actions: [
        {
          id: 'delete',
          label: 'DELETE',
          loadingLabel: 'DELETING...',
          icon: 'trash',
          handler: handleBatchDelete
        },
        {
          id: 'download',
          label: 'DOWNLOAD',
          loadingLabel: 'DOWNLOADING...',
          icon: 'download',
          handler: handleBatchDownload
        }
      ]
    });

    // Listen for save state changes from canvas
    window.addEventListener('idea:saved', (e) => {
      const { ideaId, saved } = e.detail;
      console.log('[Ideas] Received idea:saved event:', ideaId, saved);

      // Update filter counts and re-apply filters if viewing saved
      updateFilterCounts(Ideas.items.length);

      // If viewing saved filter and item was unsaved, re-apply filters to remove it
      if (CONFIG.filters.status === 'saved' && !saved) {
        applyFilters();
      }
    });

    console.log('[Ideas] Module initialized with format bar filters and batch selection');
  }

  // ---------------------------------------------------------------------------
  // BATCH OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Handle batch delete of selected ideas
   * Uses deep delete API to remove DB records + R2 files
   * @param {string[]} ideaIds - Array of idea IDs to delete
   */
  async function handleBatchDelete(ideaIds) {
    const confirmed = await showConfirmModal({
      title: 'Delete Ideas',
      message: `Delete ${ideaIds.length} idea${ideaIds.length > 1 ? 's' : ''}? This will permanently remove all associated images and videos.`,
      confirmText: 'DELETE',
      danger: true
    });

    if (!confirmed) return;

    try {
      // Use API client to ensure correct backend URL
      const result = await api.batchDeleteIdeas(ideaIds);

      if (result.success) {
        // 1. Remove from local Ideas cache FIRST
        Ideas.items = Ideas.items.filter(idea => !ideaIds.includes(idea.id));

        // 2. Remove from DOM
        ideaIds.forEach(id => {
          const card = document.querySelector(`.idea-card[data-id="${id}"]`);
          if (card) {
            card.remove();
            console.log(`[Ideas] Removed card ${id} from DOM`);
          }
        });

        // 3. Clear selection
        ideasSelection.clear();

        // 4. Re-bridge state for any remaining ideas
        bridgeStateFromDB(Ideas.items);

        // 5. Re-render grid to fix layout after card removal
        applyFilters();

        // 6. Show success message with cleanup details
        const msg = result.cleanup
          ? `Deleted ${result.deleted} ideas (${result.cleanup.filesDeleted} files removed)`
          : `Deleted ${result.deleted} ideas`;
        showToast(msg, 'success');
        Sounds?.play('success');
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (err) {
      console.error('[Ideas] Batch delete error:', err);
      showToast('Failed to delete ideas', 'error');
      Sounds?.play('error');
    }
  }

  /**
   * Handle batch download of selected ideas' media files
   * Downloads images and videos from R2 storage via backend proxy
   * @param {string[]} ideaIds - Array of idea IDs to download
   */
  async function handleBatchDownload(ideaIds) {
    try {
      // Get idea data from current state
      const ideas = ideaIds.map(id => Ideas.getById(id)).filter(Boolean);

      if (ideas.length === 0) {
        showToast('No ideas found to download', 'warning');
        return;
      }

      // Collect media URLs
      const mediaItems = [];
      for (const idea of ideas) {
        const imageUrl = getImageUrl(idea);
        const videoUrl = getVideoUrl(idea);

        if (imageUrl) {
          mediaItems.push({
            url: imageUrl,
            type: 'image',
            filename: `${idea.id}-image.${getExtension(imageUrl, 'png')}`
          });
        }
        if (videoUrl) {
          mediaItems.push({
            url: videoUrl,
            type: 'video',
            filename: `${idea.id}-video.${getExtension(videoUrl, 'mp4')}`
          });
        }
      }

      if (mediaItems.length === 0) {
        showToast('No media files to download', 'warning');
        return;
      }

      // Download each file
      showToast(`Downloading ${mediaItems.length} files...`, 'info');

      let downloaded = 0;
      let failed = 0;

      for (const item of mediaItems) {
        try {
          // Use backend proxy for R2 URLs to avoid CORS issues
          const isR2Url = item.url.includes('r2.dev') || item.url.includes('r2.cloudflarestorage');
          let fetchUrl = item.url;

          if (isR2Url) {
            // Route through backend proxy
            const { API_BASE } = await import('../../../js/api/endpoints.js');
            const { getSessionToken } = await import('../../auth/js/client.js');
            const token = await getSessionToken();

            const proxyUrl = `${API_BASE}/api/ideas/media/download?url=${encodeURIComponent(item.url)}`;
            const response = await fetch(proxyUrl, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            // Trigger download
            const a = document.createElement('a');
            a.href = url;
            a.download = item.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } else {
            // Direct fetch for non-R2 URLs (e.g., fal.ai CDN)
            const response = await fetch(item.url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            // Trigger download
            const a = document.createElement('a');
            a.href = url;
            a.download = item.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }

          downloaded++;

          // Small delay between downloads to prevent browser throttling
          if (mediaItems.length > 3) {
            await new Promise(r => setTimeout(r, 200));
          }
        } catch (err) {
          console.error(`[Ideas] Failed to download ${item.filename}:`, err);
          failed++;
        }
      }

      if (failed > 0) {
        showToast(`Downloaded ${downloaded} files, ${failed} failed`, 'warning');
      } else {
        showToast(`Downloaded ${downloaded} files`, 'success');
      }
    } catch (err) {
      console.error('[Ideas] Batch download error:', err);
      showToast('Failed to download files', 'error');
    }
  }

  /**
   * Get file extension from URL
   * @param {string} url - The file URL
   * @param {string} fallback - Fallback extension
   * @returns {string}
   */
  function getExtension(url, fallback = 'bin') {
    try {
      const pathname = new URL(url).pathname;
      const ext = pathname.split('.').pop()?.toLowerCase();
      if (ext && ext.length <= 5 && /^[a-z0-9]+$/.test(ext)) {
        return ext;
      }
    } catch {}
    return fallback;
  }

  // ---------------------------------------------------------------------------
  // Auto-init
  // ---------------------------------------------------------------------------

  if (document.querySelector('#ideas-grid')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // ---------------------------------------------------------------------------
  // Add New Idea (for format conversion, etc.)
  // ---------------------------------------------------------------------------

  /**
   * Add a new idea to the dashboard grid without full reload
   * Called when a new idea is created (e.g., format conversion)
   * @param {Object} newIdea - The new idea object from the API response
   */
  function addNewIdea(newIdea) {
    if (!newIdea || !newIdea.id) {
      console.warn('[Ideas] addNewIdea: Invalid idea object');
      return;
    }

    console.log('[Ideas] Adding new idea to grid:', newIdea.id);

    // Normalize the idea for the grid
    const normalized = normalizeIdeaForGenerator(newIdea);

    // Add to Ideas.items (prepend so it shows first)
    if (Ideas && Ideas.items) {
      Ideas.items.unshift(newIdea);
    }

    // Add to GeneratorState.generatedIdeas
    if (GeneratorState.generatedIdeas && GeneratorState.generatedIdeas[0]) {
      GeneratorState.generatedIdeas[0].ideas.unshift(normalized);
    }

    // Register image URL
    const imageUrl = newIdea.imageUrl || newIdea.image_url || getImageUrl(newIdea);
    if (imageUrl) {
      if (!GeneratorState.preloadedImages) {
        GeneratorState.preloadedImages = {};
      }
      GeneratorState.preloadedImages[String(newIdea.id)] = imageUrl;
      console.log('[Ideas] Registered image for new idea:', newIdea.id);
    }

    // Update filter counts
    updateFilterCounts(Ideas.items.length);

    // Re-render the grid
    applyFilters();

    console.log('[Ideas] New idea added to grid, total:', Ideas.items.length);
  }

  // ---------------------------------------------------------------------------
  // Update Existing Idea (for refinement, etc.)
  // ---------------------------------------------------------------------------

  /**
   * Update an existing idea in the dashboard grid without full reload
   * Called when an idea is refined or edited
   * @param {Object} updatedIdea - The updated idea object from the API response
   */
  function updateIdeaCard(updatedIdea) {
    if (!updatedIdea || !updatedIdea.id) {
      console.warn('[Ideas] updateIdeaCard: Invalid idea object');
      return;
    }

    const ideaId = String(updatedIdea.id);
    console.log('[Ideas] Updating idea in grid:', ideaId);

    // Find and update in Ideas.items
    if (Ideas && Ideas.items) {
      const idx = Ideas.items.findIndex(i => String(i.id) === ideaId);
      if (idx !== -1) {
        // Merge updated data into existing item
        Object.assign(Ideas.items[idx], updatedIdea);
        console.log('[Ideas] Updated Ideas.items at index:', idx);
      }
    }

    // Update in GeneratorState.generatedIdeas
    const normalized = normalizeIdeaForGenerator(updatedIdea);
    if (GeneratorState.generatedIdeas && GeneratorState.generatedIdeas[0]) {
      const gsIdx = GeneratorState.generatedIdeas[0].ideas.findIndex(i => String(i.id) === ideaId);
      if (gsIdx !== -1) {
        GeneratorState.generatedIdeas[0].ideas[gsIdx] = normalized;
        console.log('[Ideas] Updated GeneratorState at index:', gsIdx);
      }
    }

    // Update refinedIdeas if this is a refined idea
    if (updatedIdea.isRefined || updatedIdea.is_refined) {
      if (!GeneratorState.refinedIdeas) {
        GeneratorState.refinedIdeas = {};
      }
      GeneratorState.refinedIdeas[ideaId] = normalized;
    }

    // Update preloaded images if image URL changed
    const imageUrl = updatedIdea.imageUrl || updatedIdea.image_url || getImageUrl(updatedIdea);
    if (imageUrl) {
      if (!GeneratorState.preloadedImages) {
        GeneratorState.preloadedImages = {};
      }
      GeneratorState.preloadedImages[ideaId] = imageUrl;
      console.log('[Ideas] Updated image for idea:', ideaId);
    }

    // Re-render the card in the DOM
    const existingCard = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
    if (existingCard) {
      // Get the index from the card for proper numbering
      const cardIndex = existingCard.querySelector('.idea-number')?.textContent || '';
      const newCardHtml = renderIdeaCard(normalized, cardIndex ? parseInt(cardIndex.replace('#', '')) : 0, { isDashboard: true });

      // Create a temp container to parse the HTML
      const temp = document.createElement('div');
      temp.innerHTML = newCardHtml;
      const newCard = temp.firstElementChild;

      // Replace old card with new
      existingCard.replaceWith(newCard);
      console.log('[Ideas] Replaced card in DOM:', ideaId);
    }

    console.log('[Ideas] Idea updated in grid:', ideaId);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  return {
    CONFIG,
    init,
    applyFilters,
    loadAndRender,
    deleteIdea,
    addNewIdea,
    updateIdeaCard
  };
})();

window.IdeasModule = IdeasModule;
